import { cloneElement, FC, isValidElement, useEffect, useRef } from 'react';
import { Pagination, PaginationProps } from 'antd';

export type CustomPaginationProps = Omit<PaginationProps, 'itemRender'>;

export const CustomPagination: FC<CustomPaginationProps> = (props) => {
  const { className, ...rest } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const extraControls = ref.current?.querySelector(
      '[class*="pagination-options"]'
    );

    if (!extraControls) {
      return;
    }

    const setIds = (nodes: NodeList) =>
      nodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          node
            .getElementsByTagName('input')[0]
            ?.setAttribute('id', getExtraControlId(node));
        }
      });

    setIds(extraControls.childNodes);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        setIds(addedNodes);
      });
    });
    mutationObserver.observe(extraControls, {
      childList: true,
      subtree: false,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={className}>
      <Pagination
        showQuickJumper
        showSizeChanger
        {...rest}
        itemRender={itemRender}
      />
    </div>
  );
};

const getExtraControlId = (node: HTMLElement) =>
  Array.from(node.classList).reduce(
    (id, className) =>
      className.includes('quick-jumper')
        ? 'quick-jump-to-page'
        : className.includes('size-changer')
        ? 'change-page-size'
        : id,
    node.id
  );

const itemRender: Required<PaginationProps>['itemRender'] = (
  page,
  type,
  originalElement
) => {
  const withId = (id: string) =>
    isValidElement(originalElement)
      ? cloneElement(originalElement, { id } as any)
      : originalElement;

  switch (type) {
    case 'prev':
    case 'next':
    case 'jump-prev':
    case 'jump-next':
      return withId(`${type}-page`);
    case 'page':
      return withId(`page-${page}`);
    default:
      return originalElement;
  }
};
